import { isAvailable, scan } from '@tauri-apps/plugin-nfc';
import { useCallback, useState } from 'react';

import { checkScannedUrl } from '@ui/Utils/scan/tag/allowedUrls';

interface UseNfcState {
    isAllowed: boolean | null;
    lastError: string | null;
    lastTag: Record<string, unknown> | null;
    lastUid: string | null;
    lastUrl: string | null;
    redirectUrl: string | null;
    scanning: boolean;
    supported: boolean | null;
}

export interface UseNfcReturn extends UseNfcState {
    cancel: () => void;
    checkSupport: () => Promise<boolean>;
    clear: () => void;
    scanTag: () => Promise<void>;
}

export function useNfc(): UseNfcReturn {
    const [state, setState] = useState<UseNfcState>({
        isAllowed: null,
        lastError: null,
        lastTag: null,
        lastUid: null,
        lastUrl: null,
        redirectUrl: null,
        scanning: false,
        supported: null,
    });

    const checkSupport = useCallback(async () => {
        try {
            const rawUnknown = (await isAvailable()) as unknown;
            let available = false;
            if (typeof rawUnknown === 'boolean') {
                available = rawUnknown;
            } else if (rawUnknown && typeof rawUnknown === 'object' && 'available' in rawUnknown) {
                const maybe = (rawUnknown as { available?: unknown }).available;
                if (typeof maybe === 'boolean') {
                    available = maybe;
                }
            }
            setState((s) => ({ ...s, supported: available }));
            return available;
        } catch (error) {
            const msg = (error as { message?: string }).message ?? 'NFC availability failed';
            setState((s) => ({ ...s, lastError: msg, supported: false }));
            return false;
        }
    }, []);

    const decodeNdefUriPayload = (payload: unknown): string | null => {
        if (!Array.isArray(payload)) {
            return null;
        }
        if (payload.length === 0) {
            return null;
        }
        const prefixCode: unknown = payload[0];
        if (typeof prefixCode !== 'number') {
            return null;
        }
        // NFC Forum "URI Record Type Definition" prefix codes
        const prefixMap: Record<number, string> = {
            0x00: '',
            0x01: 'http://www.',
            0x0a: 'sftp://',
            0x0b: 'smb://',
            0x0c: 'nfs://',
            0x0d: 'ftp://',
            0x0e: 'dav://',
            0x0f: 'news:',
            0x10: 'telnet://',
            0x11: 'imap:',
            0x12: 'rtsp://',
            0x13: 'urn:',
            0x02: 'https://www.',
            0x14: 'pop:',
            0x15: 'sip:',
            0x16: 'sips:',
            0x17: 'tftp:',
            0x18: 'btspp://',
            0x19: 'btl2cap://',
            0x1a: 'btgoep://',
            0x1b: 'tcpobex://',
            0x1c: 'irdaobex://',
            0x1d: 'file://',
            0x03: 'http://',
            0x1e: 'urn:epc:id:',
            0x1f: 'urn:epc:tag:',
            0x20: 'urn:epc:pat:',
            0x21: 'urn:epc:raw:',
            0x22: 'urn:epc:',
            0x23: 'urn:nfc:',
            0x04: 'https://',
            0x05: 'tel:',
            0x06: 'mailto:',
            0x07: 'ftp://anonymous:anonymous@',
            0x08: 'ftp://ftp.',
            0x09: 'ftps://',
        };
        const prefix = prefixMap[prefixCode] ?? '';
        const restBytes = payload.slice(1) as unknown[];
        const chars: string[] = [];
        for (const b of restBytes) {
            if (typeof b === 'number') {
                chars.push(String.fromCharCode(b));
            }
        }
        const suffix = chars.join('');
        const uri = prefix + suffix;
        return /^https?:\/\//i.test(uri) || prefix !== '' ? uri : null;
    };

    const extractUrl = useCallback((tag: Record<string, unknown>): string | null => {
        const direct = typeof tag.value === 'string' ? tag.value : null;
        if (direct && /^https?:\/\//i.test(direct)) {
            return direct;
        }
        const recs = (tag as { records?: unknown }).records;
        if (Array.isArray(recs)) {
            for (const r of recs) {
                if (!r || typeof r !== 'object') {
                    continue;
                }
                const ro = r as Record<string, unknown>;
                // Try NFC URI payload decoding when payload is byte array & TNF indicates well-known (tnf=1 and U record type code 0x55 in kind array)
                const kind = ro.kind;
                const tnf = ro.tnf;
                if (Array.isArray(kind) && kind.length === 1 && kind[0] === 0x55 && tnf === 1) {
                    const decoded = decodeNdefUriPayload(ro.payload);
                    if (decoded) {
                        return decoded;
                    }
                }
                for (const key of ['data', 'payload', 'value']) {
                    const v = ro[key];
                    if (typeof v === 'string' && /^https?:\/\//i.test(v)) {
                        return v;
                    }
                    // If payload is numeric array but we did not match decoded above, attempt generic decode as ASCII
                    if (key === 'payload' && Array.isArray(v)) {
                        const ascii = v.every((x) => typeof x === 'number')
                            ? v.map((n: unknown) => (typeof n === 'number' ? String.fromCharCode(n) : '')).join('')
                            : '';
                        if (/^https?:\/\//i.test(ascii)) {
                            return ascii;
                        }
                    }
                }
            }
        }
        for (const v of Object.values(tag)) {
            if (typeof v === 'string' && /^https?:\/\//i.test(v)) {
                return v;
            }
        }
        return null;
    }, []);

    const scanTag = useCallback(async () => {
        if (state.scanning) {
            return;
        }
        setState((s) => ({ ...s, lastError: null, scanning: true }));
        try {
            const raw = await scan(
                { type: 'tag' },
                {
                    keepSessionAlive: false,
                    message: 'Scan a NFC tag',
                    successMessage: 'Tag scanned',
                },
            );
            let tagObj: Record<string, unknown>;
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (raw !== null && typeof raw === 'object') {
                tagObj = raw as unknown as Record<string, unknown>;
            } else {
                tagObj = { value: raw as unknown };
            }
            const url = extractUrl(tagObj);
            let uid: string | null = null;
            const idField = (tagObj as { id?: unknown }).id;
            if (Array.isArray(idField) && idField.every((x) => typeof x === 'number')) {
                uid = idField
                    .map((b: number) => {
                        const v = b & 0xff;
                        return v.toString(16).padStart(2, '0');
                    })
                    .join(':')
                    .toUpperCase();
            }
            const scannedUrl = checkScannedUrl(url);
            setState((s) => ({
                ...s,
                isAllowed: scannedUrl.isAllowed,
                lastError: scannedUrl.errorMessage ?? null,
                lastTag: tagObj,
                lastUid: uid,
                lastUrl: url ?? null,
                redirectUrl: scannedUrl.redirectUrl,
                scanning: false,
            }));
        } catch (error) {
            const msg = (error as { message?: string }).message ?? 'Scan failed';
            setState((s) => ({ ...s, lastError: msg, scanning: false }));
        }
    }, [extractUrl, state.scanning]);

    const cancel = useCallback(() => {
        // There is no cancel API in current plugin for one-shot scan; we emulate by flipping state.
        setState((s) => ({ ...s, scanning: false }));
    }, []);

    const clear = useCallback(() => {
        setState((s) => ({
            ...s,
            isAllowed: null,
            lastError: null,
            lastTag: null,
            lastUid: null,
            lastUrl: null,
            redirectUrl: null,
        }));
    }, []);

    return {
        cancel,
        checkSupport,
        clear,
        isAllowed: state.isAllowed,
        lastError: state.lastError,
        lastTag: state.lastTag,
        lastUid: state.lastUid,
        lastUrl: state.lastUrl,
        redirectUrl: state.redirectUrl,
        scanning: state.scanning,
        scanTag,
        supported: state.supported,
    };
}
